import { useCallback } from 'react';

import {
  SLACK_GROUP_CHANNEL_TYPES,
  SlackChannelGroup,
  SlackChannelResult,
  SlackChannelType,
} from '@this+that/models';
import log from 'loglevel';

import { drainSlackChannels } from '../../../services/slack.service';
import {
  useIntegrationsStore,
  useSlackCommunicationsStore,
} from '../../../store';
import { SLACK_WORKSPACE_FETCH_CONCURRENCY } from '../../../utils/constants';
import { mapWithConcurrency } from '../../../utils/mapWithConcurrency';

interface WorkspaceChannels {
  workspaceId?: string;
  workspaceName?: string;
  channels: SlackChannelResult[];
}

interface GroupedChannels {
  directGroups: Partial<SlackChannelGroup>[];
  channelGroups: Partial<SlackChannelGroup>[];
}

const groupWorkspaceChannels = (
  workspaces: WorkspaceChannels[],
): GroupedChannels => {
  const channelGroups: Partial<SlackChannelGroup>[] = [];
  const directGroups: Partial<SlackChannelGroup>[] = [];

  workspaces.forEach(({ workspaceId, workspaceName, channels }) => {
    channels.forEach((channel) => {
      const channelGroup: Partial<SlackChannelGroup> = {
        channelId: channel.id || '',
        channelName: channel.name || '',
        workspaceId: workspaceId || '',
        workspaceName: workspaceName || '',
        lastMessage: channel.lastMessage,
        members: channel.members || [],
      };

      if (
        SLACK_GROUP_CHANNEL_TYPES.has(channel.channelType as SlackChannelType)
      ) {
        channelGroups.push(channelGroup);
      } else if (channel.channelType === SlackChannelType.IM) {
        directGroups.push(channelGroup);
      }
    });
  });

  return { directGroups, channelGroups };
};

// saveSlackChannels replaces the whole listing, so a workspace whose drain failed has to
// contribute what it last had. An empty list would erase it from the inbox until it recovers.
const carryForwardFailedWorkspaces = (
  { directGroups, channelGroups }: GroupedChannels,
  failedWorkspaceIds: ReadonlySet<string>,
): GroupedChannels => {
  if (failedWorkspaceIds.size === 0) {
    return { directGroups, channelGroups };
  }

  const { channels, directs } = useSlackCommunicationsStore.getState();
  const isFailed = (group: SlackChannelGroup) =>
    failedWorkspaceIds.has(group.workspaceId);

  return {
    directGroups: [...directGroups, ...directs.filter(isFailed)],
    channelGroups: [...channelGroups, ...channels.filter(isFailed)],
  };
};

export const useFetchSlackChannels = () => {
  const { setLoading, saveSlackChannels } = useSlackCommunicationsStore();
  const { userSlackIntegrations } = useIntegrationsStore();

  const handleFetchSlackChannels = useCallback(async () => {
    if (!userSlackIntegrations?.length) {
      log.warn('No Slack integration found. Cannot fetch channels.');
      return;
    }

    setLoading(true);
    const failedWorkspaceIds = new Set<string>();

    try {
      const allWorkspaceChannels = await mapWithConcurrency(
        userSlackIntegrations,
        SLACK_WORKSPACE_FETCH_CONCURRENCY,
        async (integration): Promise<WorkspaceChannels> => {
          const { workspaceId, workspaceName } = integration;
          if (!workspaceId) {
            return { workspaceId, workspaceName, channels: [] };
          }

          try {
            const channels = await drainSlackChannels({
              workspaceId,
              includeLastMessage: true,
            });
            return { workspaceId, workspaceName, channels };
          } catch (error) {
            log.error(
              `Error fetching channels for workspace ${workspaceId}:`,
              error,
            );
            failedWorkspaceIds.add(workspaceId);
            return { workspaceId, workspaceName, channels: [] };
          }
        },
      );

      const { directGroups, channelGroups } = carryForwardFailedWorkspaces(
        groupWorkspaceChannels(allWorkspaceChannels),
        failedWorkspaceIds,
      );

      saveSlackChannels(directGroups, channelGroups, null);
    } catch (error) {
      log.error('Error fetching slack channels:', error);
    } finally {
      setLoading(false);
    }
  }, [userSlackIntegrations, saveSlackChannels, setLoading]);

  return {
    handleFetchSlackChannels,
  };
};
